import axios from "axios";
import { axiosConfig } from "../../utils/axios/config";
import { z } from "zod";
import {
  TaskSchema,
  TemplateTaskSchema,
  EvaluationOptionSchema,
  TaskEvaluationSchema,
  PaginatedResponseSchema,
  TemplatesUsedSchema,
} from "../types/task";
import type {
  CreateTaskInput,
  UpdateTaskInput,
  CreateEvaluationInput,
  TemplatesUsed,
} from "../types/task";

const api_task = axios.create({
  ...axiosConfig,
  baseURL: axiosConfig.baseURL + "employee_tasks/",
});

// Define filter and sort types
export interface TaskFilters {
  search?: string;
  priority?: string | string[];
  evaluation?: string | string[];
}

export type TaskSortField =
  | "priority"
  | "-priority"
  | "evaluation"
  | "-evaluation"
  | "created"
  | "-created"
  | "modified"
  | "-modified";

export const TaskAPI = {
  // Position Tasks
  getPositionTasks: async (
    positionId: number,
    page = 1,
    filters?: TaskFilters,
    ordering?: TaskSortField,
  ) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", "10");

    // Add filters
    if (filters) {
      if (filters.search) {
        params.append("search", filters.search);
      }

      if (filters.priority) {
        const priorities = Array.isArray(filters.priority)
          ? filters.priority
          : [filters.priority];
        priorities.forEach((priority) => params.append("priority", priority));
      }

      if (filters.evaluation) {
        const evaluations = Array.isArray(filters.evaluation)
          ? filters.evaluation
          : [filters.evaluation];
        evaluations.forEach((evaluation) =>
          params.append("evaluation", evaluation),
        );
      }
    }

    // Add ordering
    if (ordering) {
      params.append("ordering", ordering);
    }

    const response = await api_task.get(`/positions/${positionId}/tasks/`, {
      params,
      paramsSerializer: { serialize: (params: any) => params.toString() },
    });
    return PaginatedResponseSchema(TaskSchema).parse(response.data);
  },

  createPositionTask: async (positionId: number, data: CreateTaskInput) => {
    const response = await api_task.post(
      `/positions/${positionId}/tasks/`,
      data,
    );
    return TaskSchema.parse(response.data);
  },

  // Employee Tasks
  getEmployeeTasks: async (
    employeeId: number,
    page = 1,
    filters?: TaskFilters,
    ordering?: TaskSortField,
  ) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", "10");

    // Add filters
    if (filters) {
      if (filters.search) {
        params.append("search", filters.search);
      }

      if (filters.priority) {
        const priorities = Array.isArray(filters.priority)
          ? filters.priority
          : [filters.priority];
        priorities.forEach((priority) => params.append("priority", priority));
      }

      if (filters.evaluation) {
        const evaluations = Array.isArray(filters.evaluation)
          ? filters.evaluation
          : [filters.evaluation];
        evaluations.forEach((evaluation) =>
          params.append("evaluation", evaluation),
        );
      }
    }

    // Add ordering
    if (ordering) {
      params.append("ordering", ordering);
    }

    const response = await api_task.get(`/employees/${employeeId}/tasks/`, {
      params,
      paramsSerializer: { serialize: (params: any) => params.toString() },
    });
    return PaginatedResponseSchema(TaskSchema).parse(response.data);
  },

  getTaskPermissions: async (employeeId: number) => {
    const response = await api_task.get(
      `/employees/${employeeId}/permissions/`,
    );
    return response.data as {
      "employee_tasks.can_view_tasks": boolean;
      "employee_tasks.can_create_tasks": boolean;
      "employee_tasks.can_evaluate_task": boolean;
      "employee_tasks.can_view_evaluations": boolean;
      "employee_tasks.can_edit_task": boolean;
      "employee_tasks.can_edit_template_task": boolean;
      "employee_tasks.can_delete_task": boolean;
    };
  },

  createEmployeeTask: async (employeeId: number, data: CreateTaskInput) => {
    const response = await api_task.post(
      `/employees/${employeeId}/tasks/`,
      data,
    );
    return TaskSchema.parse(response.data);
  },

  // Task operations
  updateTask: async (taskId: string, data: UpdateTaskInput) => {
    const response = await api_task.patch(`/tasks/${taskId}/`, data);
    return TaskSchema.parse(response.data);
  },

  deleteTask: async (taskId: string) => {
    await api_task.delete(`/tasks/${taskId}/`);
  },

  ignoreTask: async (taskId: string, employeeId: number, reason?: string) => {
    const response = await api_task.post(`/tasks/${taskId}/ignore/`, {
      source_object_id: employeeId,
      reason,
    });
    return TaskSchema.parse(response.data);
  },

  // Template Tasks
  getTemplateTasks: async (search?: string, signal?: AbortSignal) => {
    const response = await api_task.get("/template-tasks/", {
      params: { search, is_active: true },
      signal,
    });
    return PaginatedResponseSchema(TemplateTaskSchema).parse(response.data);
  },

  getTemplateTask: async (id: string, signal?: AbortSignal) => {
    const response = await api_task.get(`/template-tasks/${id}/`, {
      signal,
    });
    return TemplateTaskSchema.parse(response.data);
  },

  getTemplateUsedFor: async (
    id: number,
    model: string,
    signal?: AbortSignal,
  ) => {
    const model_with_label = {
      position: "positions.position",
      employee: "employees.employee",
    }[model];

    const response = await api_task.get(`/template-tasks/get_used_templates/`, {
      signal,
      params: {
        model: model_with_label,
        id,
      },
    });
    return TemplatesUsedSchema.parse(response.data) as TemplatesUsed;
  },

  createTemplateTask: async (name: string, description?: string) => {
    const response = await api_task.post("/template-tasks/", {
      name,
      description,
    });
    return TemplateTaskSchema.parse(response.data);
  },

  // Evaluation Options
  getEvaluationOptions: async () => {
    const response = await api_task.get("/evaluation-options/");
    return PaginatedResponseSchema(EvaluationOptionSchema).parse(response.data);
  },

  // Evaluations
  createEvaluation: async (employeeId: number, data: CreateEvaluationInput) => {
    const response = await api_task.post(
      `/employees/${employeeId}/evaluations/`,
      data,
    );
    return TaskEvaluationSchema.parse(response.data);
  },

  getEmployeeEvaluations: async (employeeId: number) => {
    const response = await api_task.get(
      `/employees/${employeeId}/evaluations/`,
    );
    return z.array(TaskEvaluationSchema).parse(response.data);
  },
};
